import type { BlogPost } from '~/composables/useBlogApi'

// Keep a tiny, client-only route cache. Hover and keyboard focus can begin the
// article request before navigation, without adding speculative work during SSR.
const pendingPosts = new Map<string, Promise<BlogPost | undefined>>()

export function usePostPrefetch() {
  const cachedPosts = useState<Record<string, BlogPost>>('post-prefetch-cache', () => ({}))
  const { getPost } = useBlogApi()

  const getPrefetchedPost = (slug: string) => cachedPosts.value[String(slug || '').trim()]

  const prefetchPost = (slug: string) => {
    if (!import.meta.client) return
    const key = String(slug || '').trim()
    if (!key || cachedPosts.value[key]) return
    const pending = pendingPosts.get(key)
    if (pending) return pending

    const request: Promise<BlogPost | undefined> = getPost(key)
      .then((post) => {
        cachedPosts.value = { ...cachedPosts.value, [key]: post }
        return post
      })
      .catch(() => undefined)
      .finally(() => pendingPosts.delete(key))
    pendingPosts.set(key, request)
    return request
  }

  return { getPrefetchedPost, prefetchPost }
}
